import { minutesToCreditsDuration } from '@/environment';
import { Page } from '@/pages/AdvancedEditor/AdvancedEditor';
import { getMaskingType } from '@/pages/AdvancedEditor/components/panels/avatars/ModifyPresenter/controls';
import {
    getPageProperty,
    PolotnoStoreActivePageKeys,
    PolotnoStoreKeys,
    useCurrentProjectId,
    usePolotnoState,
} from '@/pages/AdvancedEditor/hooks';
import { useGetCurrentPresenter } from '@/pages/AdvancedEditor/hooks/useGetCurrentPresenter';
import { polotnoTypes } from '@/pages/AdvancedEditor/types';
import {
    checkPolotnoType,
    countChildrenByType,
    countChildrenFromTemplate,
    getAvatarElementFromPage,
    getUsedTemplates,
} from '@/pages/AdvancedEditor/utils';
import { useCreditQuery } from '@/state/credits/credits';
import { useCurrentLibraryItems } from '@/state/video-library/videoLibrary';
import { CropMode, Emotion } from '@/utils/types';
import { getPresenterType, ImageType, IVoice, ScriptType, VoiceConfig } from '@d-id/types';
import { StoreType } from 'polotno/model/store';

const isBackgroundRemoved = (page: Page) => {
    const avatar = page.children?.find(child => checkPolotnoType(child, polotnoTypes.Avatar));
    return !!avatar?.id?.includes('-removed_bg');
};

const getSelectedBackground = (page: Page) => {
    const background = page.background;
    const hasBackground = page.background != 'white';

    if (!hasBackground || !background) {
        return 'False';
    }
    if (background.includes('create-images-results')) {
        return 'Upload';
    }
    if (background.includes('images.unsplash')) {
        return 'Gallery';
    }
    return 'Color';
};

interface GenerationMixpanelData {
    projectId: string;
    hasEnoughCredits: boolean;
    creditsCost: number;
    remainingCredits: number | undefined;
    totalDuration: number;
    scenesCount: number;
    scenes: {
        duration: number;
        textLength: number;
        driver: string | undefined;
        presenter: string | undefined;
        type: string;
        presenterType: string;
        isDefault: boolean | undefined;
        selectedScript: ScriptType;
        selectedVoice: IVoice | null;
        selectedStyle: VoiceConfig;
        selectedEmotion: Emotion | undefined;
        canvasLayout: CropMode;
        librarySize: number;
        hasText: boolean;
        hasBackground: string;
        isBackgroundRemoved: boolean;
    }[];
    elementCount: number;
    elementCountTemplate: number;
    templatedPercentage: number;
    templateName: string[];
    templateId: string[];
}

export const getTotalVideoDuration = (store: StoreType): number => {
    return store.pages.reduce((acc, page) => acc + page.duration || 0, 0);
};

export const useGenerationMixpanelData = (store: StoreType): GenerationMixpanelData => {
    const [cropSettings] = usePolotnoState(PolotnoStoreKeys.cropSettings);
    const presenter = useGetCurrentPresenter();
    const { data: userCredit } = useCreditQuery();

    const videoDuration = getTotalVideoDuration(store);
    const creditsCost = videoDuration ? Math.ceil(videoDuration / 1000 / minutesToCreditsDuration) : 0;
    const hasEnoughCredits = (userCredit?.remaining ?? 0) >= (creditsCost ?? 0);
    const projectId = useCurrentProjectId();

    const { items } = useCurrentLibraryItems();

    const elementCount = countChildrenByType(store);
    const elementCountTemplate = countChildrenFromTemplate(store);
    const templatedPercentage = Math.round((elementCountTemplate / elementCount) * 100);
    const { templateIds, templateNames } = getUsedTemplates(store);

    return {
        projectId,
        hasEnoughCredits,
        creditsCost,
        remainingCredits: userCredit?.remaining || undefined,
        totalDuration: videoDuration / 1000,
        scenesCount: store.pages?.length || 0,
        scenes: store.pages.map(page => {
            const selectedScript = getPageProperty(page, PolotnoStoreActivePageKeys.selectedScript);
            const durationInSeconds = Math.round(page.duration || 0) / 1000;
            const avatar = getAvatarElementFromPage(page);

            return {
                duration: durationInSeconds,
                textLength: getPageProperty(page, PolotnoStoreActivePageKeys.textInput).trim().length,
                driver: presenter?.driverId,
                presenter: presenter?.url,
                type: getPresenterType(presenter),
                presenterType: presenter?.presenterType === ImageType.Upload ? ImageType.Upload : 'default',
                presenterLabels: presenter?.labels,
                isDefault: presenter?.isDefault,
                selectedScript,
                selectedVoice: getPageProperty(page, PolotnoStoreActivePageKeys.ttsSelectedVoice),
                selectedStyle: getPageProperty(page, PolotnoStoreActivePageKeys.ttsVoiceConfig),
                selectedEmotion: getPageProperty(page, PolotnoStoreActivePageKeys.selectedEmotion),
                canvasLayout: cropSettings,
                librarySize: items.length,
                hasText: page.children?.find(child => checkPolotnoType(child, polotnoTypes.Text)),
                hasBackground: getSelectedBackground(page),
                isBackgroundRemoved: isBackgroundRemoved(page),
                presenterId: presenter?.id,
                numberOfImages: countChildrenByType(store, polotnoTypes.Image),
                numberOfSvgs: countChildrenByType(store, polotnoTypes.Svg),
                numberOfVideos: countChildrenByType(store, polotnoTypes.Video),
                numberOfAvatars: countChildrenByType(store, polotnoTypes.Avatar),
                numberOfTexts: countChildrenByType(store, polotnoTypes.Text),
                numberOfShapes: countChildrenByType(store, polotnoTypes.Figure) + countChildrenByType(store, polotnoTypes.Line),
                totalElementsCount: countChildrenByType(store),
                ['avatar-masking-type']: avatar ? getMaskingType(avatar) : null,
                ['avatar-masking-border']:
                    avatar && getMaskingType(avatar) === 'circle' ? (avatar.custom?.borderEnabled ? 'applied' : 'not-applied') : null,
                ['avatar-masking-shadow']:
                    avatar && getMaskingType(avatar) === 'circle' ? (avatar.shadowEnabled ? 'applied' : 'not-applied') : null,
            };
        }),
        elementCount,
        elementCountTemplate,
        templatedPercentage,
        templateName: templateNames,
        templateId: templateIds,
    };
};
